import { useMemo, useState } from 'react'
import { Box, Checkbox, HStack, Text, Tooltip, VStack } from '@chakra-ui/react'
import type { TeamFilter, TodoItem, WorkoutItem } from '../mock-data'
import { getWeekDays, todayIso } from '../week-utils'

interface ICalendarTodoSectionProps {
  workouts: WorkoutItem[]
  todos: TodoItem[]
  teamFilters: TeamFilter[]
  activeOrgId: string
}

type Tab = 'calendar' | 'todo'

// MobileCalendarTodo: extracted from the original component body so the
// desktop branch (rendered by index.tsx via CalendarPanel + TodoSidebar) can
// bypass this entirely without risking regressions to the mobile layout.
export const MobileCalendarTodo = ({
  workouts,
  todos,
  teamFilters,
  activeOrgId
}: ICalendarTodoSectionProps) => {
  const [tab, setTab] = useState<Tab>('calendar')
  const week = useMemo(() => getWeekDays(), [])
  const [selectedDate, setSelectedDate] = useState<string>(() => todayIso())
  const [localTodos, setLocalTodos] = useState<TodoItem[]>(todos)

  const dayWorkouts = workouts.filter(
    w =>
      w.date === selectedDate &&
      (activeOrgId === 'all' || w.orgId === activeOrgId)
  )
  const daysWithWorkouts = useMemo(() => new Set(workouts.map(w => w.date)), [workouts])

  const orgById = useMemo(
    () => Object.fromEntries(teamFilters.map(t => [t.id, t])),
    [teamFilters]
  )

  const toggleTodo = (id: string) =>
    setLocalTodos(prev => prev.map(t => (t.id === id ? { ...t, completed: !t.completed } : t)))

  return (
    <VStack spacing="12px" align="stretch">
      <HStack spacing={0} borderBottom="1px solid rgba(255,255,255,0.08)">
        {(['calendar', 'todo'] as Tab[]).map(t => {
          const isActive = tab === t
          return (
            <Box
              key={t}
              as="button"
              type="button"
              onClick={() => setTab(t)}
              flex={1}
              py="10px"
              bg="transparent"
              border="none"
              borderBottom="2px solid"
              borderBottomColor={isActive ? '#4DB3FF' : 'transparent'}
              color={isActive ? '#4DB3FF' : 'rgba(255,255,255,0.55)'}
              fontSize="13px"
              fontWeight={600}
              lineHeight="16px"
              _focus={{ boxShadow: 'none' }}
              _focusVisible={{ boxShadow: 'none' }}
            >
              {t === 'calendar' ? 'Calendar' : 'To-Do'}
            </Box>
          )
        })}
      </HStack>

      {tab === 'calendar' ? (
        <>
          <Box
            bg="rgba(27, 42, 63, 0.60)"
            border="1px solid rgba(194, 199, 214, 0.10)"
            borderRadius="16px"
            p="10px 8px"
          >
            <HStack spacing="4px" justify="space-between">
              {week.map(d => {
                const isSelected = d.iso === selectedDate
                const hasDot = isSelected || daysWithWorkouts.has(d.iso)
                return (
                  <Box
                    key={d.iso}
                    as="button"
                    type="button"
                    onClick={() => setSelectedDate(d.iso)}
                    flex={1}
                    bg="transparent"
                    border="none"
                    _focus={{ boxShadow: 'none' }}
                    _focusVisible={{ boxShadow: 'none' }}
                  >
                    <VStack spacing="6px">
                      <VStack
                        spacing="6px"
                        p="6px"
                        w="36px"
                        minH="64px"
                        mx="auto"
                        justify="center"
                        borderRadius="12px"
                        bgGradient={isSelected ? 'linear(to-b, #2E8FCC, #093E76)' : undefined}
                        boxShadow={isSelected ? '0 4px 10px rgba(0, 51, 102, 0.40)' : 'none'}
                      >
                        <Text
                          fontSize="10px"
                          color={isSelected ? 'white' : 'rgba(255,255,255,0.55)'}
                          m={0}
                          letterSpacing="0.05em"
                          lineHeight="1"
                        >
                          {d.label}
                        </Text>
                        <Text
                          fontSize="14px"
                          fontWeight={isSelected ? 700 : 500}
                          color={isSelected ? 'white' : 'rgba(255,255,255,0.85)'}
                          m={0}
                          lineHeight="1.1"
                        >
                          {d.dayOfMonth}
                        </Text>
                      </VStack>
                      <Box
                        w="4px"
                        h="4px"
                        flexShrink={0}
                        borderRadius="16777200px"
                        bg={hasDot ? '#4DB3FF' : 'transparent'}
                      />
                    </VStack>
                  </Box>
                )
              })}
            </HStack>
          </Box>

          <VStack spacing="10px" align="stretch">
            {dayWorkouts.length === 0 ? (
              <Box
                p="20px"
                borderRadius="12px"
                border="1px solid rgba(255,255,255,0.08)"
                textAlign="center"
              >
                <Text color="rgba(255,255,255,0.55)" fontSize="13px" m={0}>
                  No workouts scheduled
                </Text>
              </Box>
            ) : (
              dayWorkouts.map(w => {
                const team = orgById[w.orgId]
                const accent = team?.color ?? '#4DB3FF'
                return (
                  <Box
                    key={w.id}
                    bg="#101a2d"
                    border="1px solid rgba(194, 199, 214, 0.08)"
                    borderLeft="3px solid"
                    borderLeftColor={accent}
                    borderRadius="12px"
                    p="12px 14px"
                  >
                    <HStack justify="space-between" align="flex-start" spacing="12px">
                      <VStack spacing="4px" align="flex-start" flex={1} minW={0}>
                        <Text color="white" fontSize="15px" fontWeight={600} m={0} lineHeight="1.2">
                          {w.title}
                        </Text>
                        <Text color="rgba(255,255,255,0.65)" fontSize="12px" m={0}>
                          🕐 {w.startTime} - {w.endTime}
                        </Text>
                        <Text color="rgba(255,255,255,0.65)" fontSize="12px" m={0}>
                          📍 {w.location}
                        </Text>
                      </VStack>
                      {team && (
                        <Box
                          px="10px"
                          h="22px"
                          borderRadius="6px"
                          bg={`${accent}22`}
                          display="inline-flex"
                          alignItems="center"
                          flexShrink={0}
                        >
                          <Text fontSize="10px" fontWeight={700} color={accent} m={0} letterSpacing="0.06em">
                            {team.name.toUpperCase()}
                          </Text>
                        </Box>
                      )}
                    </HStack>
                  </Box>
                )
              })
            )}
          </VStack>
        </>
      ) : (
        <VStack spacing="10px" align="stretch">
          {localTodos.length === 0 ? (
            <Tooltip label="Coming soon — not available yet" hasArrow placement="top" bg="#1f2a3d" color="white" fontSize="12px" borderRadius="8px">
              <Box
                p="20px"
                borderRadius="12px"
                border="1px solid rgba(255,255,255,0.08)"
                textAlign="center"
                opacity={0.55}
                cursor="help"
              >
                <Text color="rgba(255,255,255,0.55)" fontSize="13px" fontWeight={600} letterSpacing="0.08em" m={0}>
                  TBD
                </Text>
              </Box>
            </Tooltip>
          ) : (
            localTodos.map(t => (
              <HStack
                key={t.id}
                spacing="12px"
                p="12px 14px"
                bg="#101a2d"
                border="1px solid rgba(255,255,255,0.08)"
                borderRadius="12px"
              >
                <Checkbox
                  isChecked={t.completed}
                  onChange={() => toggleTodo(t.id)}
                  colorScheme="blue"
                  size="lg"
                />
                <VStack spacing="2px" align="flex-start" flex={1} minW={0}>
                  <Text
                    color="white"
                    fontSize="14px"
                    fontWeight={600}
                    m={0}
                    textDecoration={t.completed ? 'line-through' : 'none'}
                    opacity={t.completed ? 0.55 : 1}
                  >
                    {t.title}
                  </Text>
                  <Text color="rgba(255,255,255,0.55)" fontSize="12px" m={0}>
                    {t.teamTag.label} · {t.due}
                  </Text>
                </VStack>
              </HStack>
            ))
          )}
        </VStack>
      )}
    </VStack>
  )
}

const CalendarTodoSection = (props: ICalendarTodoSectionProps) => <MobileCalendarTodo {...props} />

export default CalendarTodoSection
